import React, { useState, useEffect } from "react"
import { Paragraph } from "../../Components/Paragraph"
import { MultiStepFlowContainer } from "../../Components/MultiStepFlow"
import { PRIVACY_POLICY_URL } from "../../constants/textConstants"
import { useNavigate } from "react-router-dom"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import Spinner from "../../Components/Spinner"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { PLAN_STATUS } from "../../constants/planStatus"
import LockedStep from "../../Components/LockedStep"
import { isStepLocked } from "../../lib/plan"
import api from "../../services/api"
import { GREAT_MENTORS_RESOURCE_URL } from "../../constants/resources"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"
import MentorPreview from "../../Components/MentorPreview"
import Button from "../../Components/Button"
import { MAXIMUM_MENTOR_COUNT } from "../../constants/mentorCount"
import { useFormikContext } from "formik"
import MentorFields from "../../Components/MentorFields"

/**
 * AddMentorSection - Helper component that uses Formik context to add mentors
 */
const AddMentorSection = ({ planToken, isSubmitting, setIsSubmitting, refreshMentors }) => {
  const { values, setFieldValue } = useFormikContext()

  const handleAdd = async () => {
    setIsSubmitting(true)
    try {
      await api.addMentors(planToken, [
        {
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
        },
      ])
      refreshMentors()
      setFieldValue("first_name", "")
      setFieldValue("last_name", "")
      setFieldValue("email", "")
    } catch (error) {
      console.error("Error adding mentor:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mt-6 w-full">
      <MentorFields />
      <div className="flex justify-center">
        <Button
          type="button"
          bgColor="bg-circle hover:bg-circle/90"
          onClick={handleAdd}
          disabled={isSubmitting || !values.first_name || !values.last_name || !values.email}
        >
          {isSubmitting ? (
            <div className="flex flex-row items-center gap-1">
              <Spinner size="sm" />
              <span>Saving...</span>
            </div>
          ) : (
            "Add Mentor"
          )}
        </Button>
      </div>
    </div>
  )
}

/**
 * InviteMentors
 *
 * Multi-step flow for inviting mentors to join the mentorship circle.
 * This is step 4 of the setup checklist.
 */
const InviteMentors = () => {
  const { error, loading } = useOutletContextPlanActions()
  const { plan, currentStep } = useOutletContextPlan()
  const navigate = useNavigate()
  const [allMentors, setAllMentors] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const refreshMentors = () => {
    if (!plan?.token) return

    api
      .getMentors(plan.token)
      .then(response => {
        setAllMentors(response.data.mentors)
      })
      .catch(err => {
        console.error("Error fetching mentors:", err)
      })
  }

  useEffect(() => {
    refreshMentors()
  }, [plan?.token])

  const steps = [
    {
      id: "invite-mentors",
      encouragement: "📧",
      encouragementClassName: "text-3xl",
      title: "Invite Mentors",
      renderCustomContent: values => {
        return (
          <div className="mx-auto flex max-w-2xl flex-col items-center justify-center gap-4 text-center">
            <div className="flex max-w-md flex-col items-center gap-4">
              <Paragraph>
                Now it's time to bring your student's support network together.
              </Paragraph>
              <Paragraph>
                Confirm the email addresses of mentors and send invitations so they can share their
                encouragements and commitments online.
              </Paragraph>
              <MoreInfoVideoModal
                src={GREAT_MENTORS_RESOURCE_URL}
                text=""
                postText="How to Choose Great Mentors"
                title={"Choosing Great Mentors"}
                textClassName="text-brand no-underline"
              />
            </div>

            {allMentors.length > 0 ? (
              <>
                <div className="w-full">
                  <div className="mb-3 text-sm font-semibold text-gray-700">
                    Mentors ({allMentors.length}):
                  </div>
                  <MentorPreview
                    data={allMentors}
                    theme="circle"
                    deletable={true}
                    onDelete={refreshMentors}
                    isEditing={true}
                    onInvite={refreshMentors}
                    inlineEditEnabled={true}
                    onUpdate={refreshMentors}
                  />
                </div>

                {allMentors.length < MAXIMUM_MENTOR_COUNT && (
                  <div className="w-full border-t pt-4">
                    <div className="mb-2 text-sm font-semibold text-gray-700">
                      Add another mentor:
                    </div>
                    <AddMentorSection
                      planToken={plan?.token}
                      isSubmitting={isSubmitting}
                      setIsSubmitting={setIsSubmitting}
                      refreshMentors={refreshMentors}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8">
                <p className="mb-4 text-sm text-gray-600">
                  No mentors added yet. Add mentor information below:
                </p>
                <AddMentorSection
                  planToken={plan?.token}
                  isSubmitting={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                  refreshMentors={refreshMentors}
                />
              </div>
            )}
          </div>
        )
      },
    },
  ]

  if (isStepLocked(PLAN_STATUS.INVITE_MENTORS, currentStep)) {
    return <LockedStep />
  }

  return (
    <>
      {loading && <Spinner overlay message="Inviting Mentors..." />}
      <MultiStepFlowContainer
        hideCompleteButton={true}
        steps={steps}
        initialValues={{
          first_name: "",
          last_name: "",
          email: "",
        }}
        onComplete={undefined}
        onBack={() => {
          navigate(`/plan/${plan?.token}/checklist`)
          return true
        }}
        showProgress={false}
        alwaysShowBackButton={true}
        stepId="invite-mentors"
        fieldWidth="w-64 mx-auto"
        fieldContainerClassName="flex flex-col items-center"
        customContentAfterButton={
          <>
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4 text-left">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <i className="fa-solid fa-circle-exclamation text-red-400"></i>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error.message}</h3>
                    {error.errors && (
                      <div className="mt-2 text-sm text-red-700">
                        <ul className="list-disc space-y-1 pl-5">
                          {Object.entries(error.errors).map(([field, messages]) => (
                            <li key={field}>
                              {Array.isArray(messages) ? messages.join(", ") : messages}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="text-center text-sm">
              By inviting mentors, you agree to our{" "}
              <a href={PRIVACY_POLICY_URL} className="text-gray-600 hover:underline">
                Privacy Policy
              </a>
            </div>
          </>
        }
      />
    </>
  )
}

export default InviteMentors
